import { ScreenSize, UI } from "@wwimmo/ui";
import styles from "./NotificationStatusModalList.module.css";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { uuid4 } from "@sentry/utils";
import { RootStoreContext } from "src/stores/RootStore";
import { GetDataAssemblyNotification } from "./GetDataAssemblyNotification";
import { GetDataNewsNotification } from "./GetDataNewsNotification";
import { INotificationStatus } from "src/stores/NotificationStore";
import { getDateAndTime } from "src/utils/Date";

export interface NotificationStatusListProps {
    notificationStatusList: INotificationStatus[];
}

export enum NOTIFICATIONSTATE {
    NEWS = 0,
    ASSEMBLY = 1
}

export const NotificationStatusModalList = (props: NotificationStatusListProps) => {
    const { t } = useTranslation();
    const { uiStore, notificationStore } = useContext(RootStoreContext);

    const [allNotifications, setAllNotifications] = useState<INotificationStatus[]>(props.notificationStatusList);
    const [modalNotificationStatus, setModalNotificationStatus] = useState<INotificationStatus[]>(
        props.notificationStatusList
    );

    let notificationStateData: any;
    if (notificationStore.isNewsOrAssemblyState === NOTIFICATIONSTATE.NEWS) {
        notificationStateData = GetDataNewsNotification();
    } else if (notificationStore.isNewsOrAssemblyState === NOTIFICATIONSTATE.ASSEMBLY) {
        notificationStateData = GetDataAssemblyNotification();
    }

    useEffect(() => {
        if (Array.isArray(notificationStateData) && notificationStateData.length > 0) {
            const notificationStates: INotificationStatus[] = notificationStateData;
            setAllNotifications(notificationStates);
            setModalNotificationStatus(notificationStates);
        }
        // useEffect only re-render when notificationStateData.loading is changed
        // eslint-disable-next-line
    }, [notificationStateData.loading]);

    const getStatusAndStatusSince = useCallback(
        (state: number) => {
            let userStatus = "";

            switch (state) {
                case 1:
                    userStatus = t("screens.realestate.notifications.states.not_registered");
                    break;
                case 2:
                    userStatus = t("screens.realestate.notifications.states.not_notified");
                    break;
                case 3:
                    userStatus = t("screens.realestate.notifications.states.notified");
                    break;
                case 4:
                    userStatus = t("screens.realestate.notifications.states.read_notification");
                    break;
                case 5:
                    userStatus = t("screens.realestate.notifications.states.viewd_assembly");
                    break;
                default:
                    userStatus = t("screens.realestate.notifications.states.not_registered");
            }

            return userStatus;
        },
        [t]
    );

    const CellText = ({ text, className }: { text: string; className?: string }) => {
        const ref = useRef<HTMLSpanElement>(null);
        const [isOverflowing, setIsOverflowing] = useState(false);

        useEffect(() => {
            const el = ref.current;
            if (!el) return;

            const checkOverflow = () => {
                setIsOverflowing(el.scrollWidth > el.clientWidth);
            };

            checkOverflow();

            // Re-check on resize (important for responsive modal)
            const resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(el);

            return () => resizeObserver.disconnect();
        }, [text]);

        return (
            <span ref={ref} className={className} title={isOverflowing ? text : undefined}>
                {text}
            </span>
        );
    };

    const renderNotificationStatusItem = useCallback(
        (notificationStatus: INotificationStatus, screenSize: ScreenSize) => {
            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    return (
                        <UI.List.Item
                            key={uuid4()}
                            screenSize={screenSize}
                            rows={[
                                <UI.List.Row key={"r-1"}>
                                    <UI.List.Cell
                                        key={"c-1"}
                                        colspan={1.5}
                                        className={styles.CellFix}
                                        value={
                                            <CellText
                                                text={notificationStatus.name ?? ""}
                                                className={styles.Ellipsis}
                                            />
                                        }
                                    />

                                    <UI.List.Cell
                                        key={"c-2"}
                                        colspan={1.5}
                                        className={styles.CellFix}
                                        value={
                                            <CellText
                                                text={notificationStatus.email ?? ""}
                                                className={styles.Ellipsis}
                                            />
                                        }
                                    />

                                    <UI.List.Cell
                                        key={"c-3"}
                                        colspan={1}
                                        className={styles.CellFix}
                                        value={
                                            <CellText
                                                text={getDateAndTime(notificationStatus.date || "") || ""}
                                                className={styles.Ellipsis}
                                            />
                                        }
                                    />

                                    <UI.List.Cell
                                        key={"c-4"}
                                        colspan={1.2}
                                        className={styles.CellFix}
                                        value={
                                            <CellText
                                                text={getStatusAndStatusSince(notificationStatus.state)}
                                                className={styles.Ellipsis}
                                            />
                                        }
                                    />
                                </UI.List.Row>
                            ]}
                        />
                    );

                case ScreenSize.MOBILE || window.innerWidth <= 1024:
                    return (
                        <UI.List.Item
                            key={uuid4()}
                            screenSize={screenSize}
                            rows={[
                                <UI.List.Row key="m-r-1">
                                    <UI.List.Cell
                                        colspan={1}
                                        value={
                                            <div className={styles.MobileItem}>
                                                <div className={styles.MobileRow}>
                                                    <div className={styles.MobileLabel}>
                                                        {t("screens.realestate.notifications.name")}
                                                    </div>
                                                    <div className={styles.MobileValueWrapLinesSoft}>
                                                        {notificationStatus.name}
                                                    </div>
                                                </div>

                                                <div className={styles.MobileRow}>
                                                    <div className={styles.MobileLabel}>
                                                        {t("screens.realestate.notifications.email")}
                                                    </div>
                                                    <div className={styles.MobileValueWrapLines}>
                                                        {notificationStatus.email}
                                                    </div>
                                                </div>

                                                <div className={styles.MobileRow}>
                                                    <div className={styles.MobileLabel}>
                                                        {t("screens.realestate.notifications.date")}
                                                    </div>
                                                    <div className={styles.MobileValue}>
                                                        {getDateAndTime(notificationStatus.date || "")}
                                                    </div>
                                                </div>

                                                <div className={styles.MobileRow}>
                                                    <div className={styles.MobileLabel}>
                                                        {t("screens.realestate.notifications.state")}
                                                    </div>
                                                    <div className={styles.MobileValue}>
                                                        {getStatusAndStatusSince(notificationStatus.state)}
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    />
                                </UI.List.Row>
                            ]}
                        />
                    );

                default:
                    return (
                        <UI.List.Item
                            key={uuid4()}
                            screenSize={screenSize}
                            rows={[
                                <UI.List.Row key="d-r-1">
                                    <UI.List.Cell colspan={1} value={notificationStatus.name} />
                                </UI.List.Row>
                            ]}
                        />
                    );
            }
        },
        [getStatusAndStatusSince, t]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.DESKTOP) {
                return (
                    <UI.List.Header
                        rows={[
                            <UI.List.Row key="row-1">
                                <UI.List.Cell colspan={1.5} value={t("screens.realestate.notifications.name")} />
                                <UI.List.Cell colspan={1.5} value={t("screens.realestate.notifications.email")} />
                                <UI.List.Cell colspan={1} value={t("screens.realestate.notifications.date")} />
                                <UI.List.Cell colspan={1.2} value={t("screens.realestate.notifications.state")} />
                            </UI.List.Row>
                        ]}
                    />
                );
            }

            return <UI.List.Header rows={[]} />;
        },
        [t]
    );

    const handleSearchInput = useCallback(
        ({ searchQuery }: { searchQuery?: string }) => {
            if (!searchQuery || searchQuery.trim() === "") {
                setModalNotificationStatus(allNotifications);
                return;
            }

            const query = searchQuery.toLowerCase();
            const filtered = allNotifications.filter((notification) => {
                return (
                    notification.name?.toLowerCase().includes(query) ||
                    notification.email?.toLowerCase().includes(query) ||
                    getStatusAndStatusSince(notification.state)?.toLowerCase().includes(query) ||
                    getDateAndTime(notification.date || "")
                        ?.toLowerCase()
                        .includes(query)
                );
            });

            setModalNotificationStatus(filtered);
        },
        [allNotifications, getStatusAndStatusSince]
    );

    return (
        <div>
            <div className={styles.ModalTitle}>{t("screens.realestate.notifications.modal_title")}</div>
            <UI.List.BasicList
                className={styles.NotificationStatusList}
                screenSize={uiStore.isMobile || window.innerWidth <= 1024 ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={modalNotificationStatus}
                renderItem={renderNotificationStatusItem}
                renderHeader={renderHeader}
                itemsPerPage={10}
                onSearch={handleSearchInput}
            />
        </div>
    );
};

export default NotificationStatusModalList;
